import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@/contexts/UserContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { ResumenCertificado } from '@/components/ResumenCertificado';
import type { ResumenCertificado as Resumen } from '@shared/certificados';
import {
  CheckCircle2,
  XCircle,
  AlertCircle,
  Shield,
  TrendingUp,
  Clock,
  Send,
  Settings,
  FileText,
  Loader2,
  Info,
} from 'lucide-react';

interface ComplianceStatus {
  modalidadConfigurada: boolean;
  certificadoValido: boolean;
  certificado?: Resumen | null;
  cadenaIntegridadValida: boolean;
  ultimoEnvioExitoso?: string;
  facturasPendientesEnvio: number;
  alertas: Array<{
    tipo: 'error' | 'warning' | 'info';
    mensaje: string;
    timestamp: string;
  }>;
}

interface DashboardStats {
  totalFacturas: number;
  facturasRegistradas: number;
  facturasEnviadas: number;
  ultimoEnvio?: string;
}

export function VerifactuDashboard() {
  const { fetchWithAuth } = useUser();
  const navigate = useNavigate();
  const [status, setStatus] = useState<ComplianceStatus | null>(null);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [statusRes, facturasRes] = await Promise.all([
        fetchWithAuth('/verifactu/status'),
        fetchWithAuth('/facturas'),
      ]);

      if (statusRes.ok) {
        const statusData = await statusRes.json();
        setStatus(statusData);
      }

      if (facturasRes.ok) {
        const facturas = await facturasRes.json();
        const registradas = facturas.filter((f: any) => f.verifactu).length;
        const enviadas = 0; // TODO: calcular desde logs de envío

        setStats({
          totalFacturas: facturas.length,
          facturasRegistradas: registradas,
          facturasEnviadas: enviadas,
        });
      }
    } catch (error) {
      console.error('Error loading Verifactu dashboard:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  if (!status) {
    return (
      <Card>
        <CardContent className="py-6">
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              No se pudo cargar el estado de cumplimiento Verifactu
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  // Calcular estado general de cumplimiento
  const cumplimientoTotal =
    (status.modalidadConfigurada ? 33 : 0) +
    (status.certificadoValido ? 33 : 0) +
    (status.cadenaIntegridadValida ? 34 : 0);

  const estadoGeneral =
    cumplimientoTotal === 100
      ? 'excellent'
      : cumplimientoTotal >= 66
      ? 'good'
      : cumplimientoTotal >= 33
      ? 'warning'
      : 'critical';

  const getEstadoColor = () => {
    switch (estadoGeneral) {
      case 'excellent':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'good':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'warning':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'critical':
        return 'text-red-600 bg-red-50 border-red-200';
    }
  };

  const getEstadoIcon = () => {
    switch (estadoGeneral) {
      case 'excellent':
        return <CheckCircle2 className="h-8 w-8 text-green-600" />;
      case 'good':
        return <Shield className="h-8 w-8 text-blue-600" />;
      case 'warning':
        return <AlertCircle className="h-8 w-8 text-yellow-600" />;
      case 'critical':
        return <XCircle className="h-8 w-8 text-red-600" />;
    }
  };

  const getEstadoTexto = () => {
    switch (estadoGeneral) {
      case 'excellent':
        return 'Cumplimiento Completo';
      case 'good':
        return 'Cumplimiento Parcial';
      case 'warning':
        return 'Requiere Atención';
      case 'critical':
        return 'Acción Necesaria';
    }
  };

  return (
    <div className="space-y-6">
      {/* Estado General */}
      <Card className={`border-2 ${getEstadoColor()}`}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {getEstadoIcon()}
              <div>
                <CardTitle className="text-2xl">{getEstadoTexto()}</CardTitle>
                <CardDescription className="text-base">
                  Sistema de facturación Verifactu
                </CardDescription>
              </div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold">{cumplimientoTotal}%</div>
              <div className="text-sm text-muted-foreground">Cumplimiento</div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Progress value={cumplimientoTotal} className="h-3" />
        </CardContent>
      </Card>

      {/* Métricas Clave */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Facturas Registradas</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats?.facturasRegistradas || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              de {stats?.totalFacturas || 0} totales
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pendientes de Envío</CardTitle>
            <Send className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {status.facturasPendientesEnvio}
            </div>
            <p className="text-xs text-muted-foreground">
              {status.facturasPendientesEnvio > 0 ? 'Requieren envío a AEAT' : 'Todo al día'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Último Envío</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-lg font-bold">
              {status.ultimoEnvioExitoso ? (
                new Date(status.ultimoEnvioExitoso).toLocaleDateString('es-ES', {
                  day: '2-digit',
                  month: 'short',
                })
              ) : (
                'Sin envíos'
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {status.ultimoEnvioExitoso
                ? new Date(status.ultimoEnvioExitoso).toLocaleTimeString('es-ES', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })
                : 'No hay envíos registrados'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Checklist de Cumplimiento */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Checklist de Cumplimiento
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-muted/50 rounded">
            <span className="text-sm font-medium">Modalidad Configurada</span>
            {status.modalidadConfigurada ? (
              <CheckCircle2 className="h-5 w-5 text-green-600" />
            ) : (
              <XCircle className="h-5 w-5 text-red-600" />
            )}
          </div>

          <div className="flex items-center justify-between p-3 bg-muted/50 rounded">
            <div>
              <span className="block text-sm font-medium">Certificado Digital Válido</span>
              <span className="block text-xs">
                <ResumenCertificado certificado={status.certificado} valido={status.certificadoValido} />
              </span>
            </div>
            {status.certificadoValido ? (
              <CheckCircle2 className="h-5 w-5 text-green-600" />
            ) : (
              <XCircle className="h-5 w-5 text-red-600" />
            )}
          </div>

          <div className="flex items-center justify-between p-3 bg-muted/50 rounded">
            <span className="text-sm font-medium">Cadena de Integridad</span>
            {status.cadenaIntegridadValida ? (
              <CheckCircle2 className="h-5 w-5 text-green-600" />
            ) : (
              <XCircle className="h-5 w-5 text-red-600" />
            )}
          </div>
        </CardContent>
      </Card>

      {/* Alertas */}
      {status.alertas && status.alertas.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              Alertas y Avisos
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {status.alertas.map((alerta, idx) => (
              <Alert
                key={idx}
                variant={alerta.tipo === 'error' ? 'destructive' : 'default'}
                className={
                  alerta.tipo === 'warning'
                    ? 'border-yellow-200 bg-yellow-50'
                    : alerta.tipo === 'info'
                    ? 'border-blue-200 bg-blue-50'
                    : ''
                }
              >
                <AlertTitle className="text-sm font-semibold">
                  {alerta.tipo === 'error'
                    ? 'Error'
                    : alerta.tipo === 'warning'
                    ? 'Advertencia'
                    : 'Información'}
                </AlertTitle>
                <AlertDescription className="text-sm">{alerta.mensaje}</AlertDescription>
              </Alert>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Acciones Rápidas */}
      <Card>
        <CardHeader>
          <CardTitle>Acciones Rápidas</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => navigate('/configuracion')}
            className="gap-2"
          >
            <Settings className="h-4 w-4" />
            Configurar Verifactu
          </Button>

          {status.facturasPendientesEnvio > 0 && (
            <Button onClick={() => navigate('/facturas')} className="gap-2">
              <Send className="h-4 w-4" />
              Enviar Facturas Pendientes
            </Button>
          )}

          <Button
            variant="outline"
            onClick={() => navigate('/certificados')}
            className="gap-2"
          >
            <Shield className="h-4 w-4" />
            Gestionar Certificados
          </Button>

          <Button
            variant="outline"
            onClick={() => navigate('/auditoria')}
            className="gap-2"
          >
            <TrendingUp className="h-4 w-4" />
            Ver Auditoría
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
